import React, { useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAbortableLoad, isCanceled } from '../hooks/useAbortableLoad';
import { tienePermiso } from '../utils/navPermisos';
import { useThemeColors, getPrimaryButtonStyles } from '../hooks/useThemeColors';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Edit, Trash2, Loader2, FolderTree, AlertCircle,
  CornerDownRight, ChevronsUpDown, Check, Download, Upload, CheckCircle2, XCircle,
} from 'lucide-react';

const TIPOS = ['Bloque', 'Edificio', 'Etapa', 'Manzana', 'Zona'];

const SubdivisionesPage = () => {
  const { api, user } = useAuth();
  const esSuper = user?.rol === 'SUPERADMIN';
  const puedeCrear = esSuper || tienePermiso(user, 'proyectos', 'crear');
  const puedeEditar = esSuper || tienePermiso(user, 'proyectos', 'editar');
  const puedeEliminar = esSuper || tienePermiso(user, 'proyectos', 'eliminar');
  const canEdit = puedeEditar || puedeEliminar;
  const navigate = useNavigate();
  const themeColors = useThemeColors();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [subdivisiones, setSubdivisiones] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [filtroProyecto, setFiltroProyecto] = useState(searchParams.get('proyecto') || '');
  const [filtroOpen, setFiltroOpen] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [perteneceOpen, setPerteneceOpen] = useState(false);

  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const emptyForm = {
    proyecto_id: '', parent_id: '', nombre: '', csd: '',
    personalizar: false, codigo: '',
    tipo: '', numero_pisos: '', observaciones: '',
  };
  const [formData, setFormData] = useState(emptyForm);

  const proyectosConCodigo = proyectos.filter((p) => (p.codigo_proyecto || '').trim());

  const loadProyectos = useCallback(async (signal) => {
    try {
      const res = await api.get('/proyectos', { signal });
      setProyectos(res.data || []);
    } catch (e) {
      if (isCanceled(e)) return;
      toast.error('Error al cargar proyectos');
    }
  }, [api]);

  const loadSubdivisiones = useCallback(async (signal) => {
    setLoading(true);
    try {
      const params = filtroProyecto ? { proyecto_id: filtroProyecto } : {};
      const res = await api.get('/subdivisiones', { params, signal });
      setSubdivisiones(res.data || []);
    } catch (e) {
      if (isCanceled(e)) return;
      toast.error('Error al cargar subdivisiones');
    } finally {
      setLoading(false);
    }
  }, [api, filtroProyecto]);

  useAbortableLoad((signal) => loadProyectos(signal), [loadProyectos]);
  useAbortableLoad((signal) => loadSubdivisiones(signal), [loadSubdivisiones]);

  // Subdivisiones del proyecto seleccionado en el modal (para "Pertenece")
  const subsDelProyecto = subdivisiones.filter((s) => s.proyecto_id === formData.proyecto_id);

  // Prefijo del código según selección
  const proyectoForm = proyectos.find((p) => p.id === formData.proyecto_id);
  const padreForm = subsDelProyecto.find((s) => s.id === formData.parent_id);
  const prefijo = padreForm ? padreForm.codigo : (proyectoForm?.codigo_proyecto || '');

  const openCreate = () => {
    setEditing(null);
    setFormData({ ...emptyForm, proyecto_id: filtroProyecto || '' });
    setError('');
    setDialogOpen(true);
  };

  const openEdit = (sub) => {
    setEditing(sub);
    // Prefijo esperado por la fórmula (padre o Código Inmobiliario del proyecto).
    const prefijoEsperado = sub.parent_codigo || sub.proyecto_codigo_inmobiliario || '';
    const sigueFormato = prefijoEsperado && (sub.codigo || '').startsWith(`${prefijoEsperado}-`);
    const csd = sigueFormato ? (sub.codigo || '').slice(prefijoEsperado.length + 1) : '';
    setFormData({
      proyecto_id: sub.proyecto_id,
      parent_id: sub.parent_id || '',
      nombre: sub.nombre || '',
      csd,
      personalizar: !sigueFormato,
      codigo: sub.codigo || '',
      tipo: sub.tipo || '',
      numero_pisos: sub.numero_pisos ?? '',
      observaciones: sub.observaciones || '',
    });
    setError('');
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.proyecto_id) { setError('Seleccione un proyecto'); return; }
    if (!formData.nombre.trim()) { setError('El nombre es obligatorio'); return; }
    if (formData.personalizar) {
      if (!formData.codigo.trim()) { setError('El código personalizado es obligatorio'); return; }
    } else if (!formData.csd.trim()) {
      setError('El código (CSD) es obligatorio'); return;
    }
    if (!formData.tipo) { setError('Seleccione el tipo de subdivisión'); return; }

    setSaving(true);
    try {
      const payload = {
        nombre: formData.nombre.trim(),
        tipo: formData.tipo,
        numero_pisos: formData.numero_pisos === '' ? null : parseInt(formData.numero_pisos, 10),
        observaciones: formData.observaciones.trim() || null,
        parent_id: formData.parent_id || null,
        personalizar: formData.personalizar,
      };
      if (formData.personalizar) {
        payload.codigo = formData.codigo.trim();
      } else {
        payload.csd = formData.csd.trim();
      }
      if (editing) {
        await api.put(`/subdivisiones/${editing.id}`, payload);
        toast.success('Subdivisión actualizada');
      } else {
        await api.post('/subdivisiones', { ...payload, proyecto_id: formData.proyecto_id });
        toast.success('Subdivisión creada');
      }
      setDialogOpen(false);
      loadSubdivisiones();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Error al guardar la subdivisión';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleExportar = async () => {
    setExporting(true);
    try {
      const params = filtroProyecto ? { proyecto_id: filtroProyecto } : {};
      const res = await api.get('/subdivisiones/exportar/excel', { params, responseType: 'blob' });
      // Respetar el nombre de archivo enviado por el backend (Content-Disposition)
      let filename = 'subdivisiones.xlsx';
      const cd = res.headers?.['content-disposition'] || res.headers?.['Content-Disposition'];
      if (cd) {
        const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
        if (m && m[1]) filename = decodeURIComponent(m[1]);
      }
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Error al exportar');
    } finally {
      setExporting(false);
    }
  };

  const handleImportClick = () => fileInputRef.current?.click();

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/subdivisiones/importar/excel', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(res.data);
      if (!res.data.abortado) {
        toast.success(`Importación completa: ${res.data.insertadas} insertadas, ${res.data.actualizadas} actualizadas`);
        loadSubdivisiones();
      } else {
        toast.error('Importación cancelada por errores');
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Error al importar el archivo';
      setImportResult({ abortado: true, insertadas: 0, actualizadas: 0, errores: [detail] });
      toast.error('Importación cancelada');
    } finally {
      setImporting(false);
    }
  };

  const handleDelete = async (sub) => {
    if (!window.confirm(`¿Eliminar la subdivisión "${sub.codigo}"?`)) return;
    try {
      await api.delete(`/subdivisiones/${sub.id}`);
      toast.success('Subdivisión eliminada');
      loadSubdivisiones();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  const filtroLabel = filtroProyecto
    ? (() => { const p = proyectos.find((x) => x.id === filtroProyecto); return p ? `${p.nombre}${p.codigo_proyecto ? ` (${p.codigo_proyecto})` : ''}` : 'Todos los proyectos'; })()
    : 'Todos los proyectos';

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="subdivisiones-page">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <button
          onClick={() => navigate('/admin/proyectos')}
          className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 w-fit transition-colors"
          data-testid="volver-proyectos-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Volver a Proyectos
        </button>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
              <FolderTree className="w-6 h-6" /> Subdivisiones
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Configuración jerárquica de subdivisiones por proyecto
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={handleFileChange}
              data-testid="subdivision-file-input"
            />
            <Button variant="outline" size="sm" onClick={handleExportar} disabled={exporting} data-testid="exportar-subdivisiones-btn">
              {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Exportar
            </Button>
            {puedeCrear && (
              <Button variant="outline" size="sm" onClick={handleImportClick} disabled={importing} data-testid="importar-subdivisiones-btn">
                {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Importar
              </Button>
            )}
            {puedeCrear && (
              <Button
                onClick={openCreate}
                size="sm"
                className="text-white"
                data-testid="agregar-subdivision-btn"
                {...getPrimaryButtonStyles(themeColors)}
              >
                <Plus className="w-4 h-4 mr-2" /> Agregar Subdivisión
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Filtro de proyecto (combobox buscable) */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <Label className="text-xs text-slate-500 mb-1.5 block">Filtrar por proyecto</Label>
          <Popover open={filtroOpen} onOpenChange={setFiltroOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                className="w-full sm:w-96 justify-between font-normal"
                data-testid="filtro-proyecto-trigger"
              >
                <span className="truncate">{filtroLabel}</span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
              <Command>
                <CommandInput placeholder="Buscar proyecto o código..." data-testid="filtro-proyecto-search" />
                <CommandList>
                  <CommandEmpty>No se encontraron proyectos.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem value="todos" onSelect={() => { setFiltroProyecto(''); setFiltroOpen(false); }} data-testid="filtro-proyecto-todos">
                      <Check className={`mr-2 h-4 w-4 ${!filtroProyecto ? 'opacity-100' : 'opacity-0'}`} />
                      Todos los proyectos
                    </CommandItem>
                    {proyectos.map((p) => (
                      <CommandItem
                        key={p.id}
                        value={`${p.nombre} ${p.codigo} ${p.codigo_proyecto || ''}`}
                        onSelect={() => { setFiltroProyecto(p.id); setFiltroOpen(false); }}
                        data-testid={`filtro-proyecto-${p.id}`}
                      >
                        <Check className={`mr-2 h-4 w-4 ${filtroProyecto === p.id ? 'opacity-100' : 'opacity-0'}`} />
                        <span className="truncate">{p.nombre}</span>
                        {p.codigo_proyecto && <span className="ml-2 text-xs text-slate-400 font-mono">{p.codigo_proyecto}</span>}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </CardContent>
      </Card>

      {/* Tabla / Cards */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : subdivisiones.length === 0 ? (
            <div className="p-12 text-center text-slate-500" data-testid="subdivisiones-empty">
              <FolderTree className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              No hay subdivisiones registradas{filtroProyecto ? ' para este proyecto' : ''}.
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <Table className="[&_tbody_td]:py-1 [&_thead_th]:h-9">
                  <TableHeader>
                    <TableRow className="table-header">
                      <TableHead>Código</TableHead>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead className="text-center">N° Pisos</TableHead>
                      <TableHead className="hidden lg:table-cell">Proyecto</TableHead>
                      <TableHead className="hidden xl:table-cell">Pertenece</TableHead>
                      {canEdit && <TableHead></TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {subdivisiones.map((s) => (
                      <TableRow key={s.id} className="table-row" data-testid={`subdivision-row-${s.id}`}>
                        <TableCell className="font-mono text-sm font-medium">
                          <span style={{ paddingLeft: `${(s.nivel || 0) * 18}px` }} className="inline-flex items-center gap-1">
                            {s.nivel > 0 && <CornerDownRight className="w-3.5 h-3.5 text-slate-300 shrink-0" />}
                            {s.codigo}
                          </span>
                        </TableCell>
                        <TableCell className="font-medium">{s.nombre}</TableCell>
                        <TableCell><Badge variant="outline" className="font-normal">{s.tipo}</Badge></TableCell>
                        <TableCell className="text-center text-sm">{s.numero_pisos ?? '-'}</TableCell>
                        <TableCell className="hidden lg:table-cell text-sm text-slate-600">
                          {s.proyecto_codigo_inmobiliario && <span className="font-mono text-xs mr-1">{s.proyecto_codigo_inmobiliario}</span>}
                          {s.proyecto_nombre}
                        </TableCell>
                        <TableCell className="hidden xl:table-cell font-mono text-xs text-slate-500">{s.parent_codigo || '—'}</TableCell>
                        {canEdit && (
                          <TableCell>
                            <div className="flex gap-1">
                              {puedeEditar && (
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(s)} data-testid={`editar-subdivision-${s.id}`} title="Editar">
                                  <Edit className="w-3.5 h-3.5" />
                                </Button>
                              )}
                              {puedeEliminar && (
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(s)} data-testid={`eliminar-subdivision-${s.id}`} title="Eliminar">
                                  <Trash2 className="w-3.5 h-3.5 text-red-500" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden divide-y divide-slate-100">
                {subdivisiones.map((s) => (
                  <div key={s.id} className="p-4" data-testid={`subdivision-card-${s.id}`} style={{ paddingLeft: `${16 + (s.nivel || 0) * 14}px` }}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-mono text-sm font-semibold text-slate-900 flex items-center gap-1">
                          {s.nivel > 0 && <CornerDownRight className="w-3.5 h-3.5 text-slate-300 shrink-0" />}
                          {s.codigo}
                        </p>
                        <p className="text-sm text-slate-800">{s.nombre}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          <Badge variant="outline" className="font-normal mr-2">{s.tipo}</Badge>
                          {s.proyecto_nombre}
                        </p>
                      </div>
                      {canEdit && (
                        <div className="flex gap-1 shrink-0">
                          {puedeEditar && (
                            <Button variant="ghost" size="icon" onClick={() => openEdit(s)} title="Editar"><Edit className="w-4 h-4" /></Button>
                          )}
                          {puedeEliminar && (
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(s)} title="Eliminar"><Trash2 className="w-4 h-4 text-red-500" /></Button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderTree className="w-5 h-5" />
              {editing ? 'Editar Subdivisión' : 'Nueva Subdivisión'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label>Proyecto *</Label>
              <Select
                value={formData.proyecto_id}
                onValueChange={(v) => setFormData({ ...formData, proyecto_id: v, parent_id: '' })}
                disabled={!!editing}
              >
                <SelectTrigger data-testid="subdivision-proyecto-select">
                  <SelectValue placeholder="Seleccione un proyecto" />
                </SelectTrigger>
                <SelectContent>
                  {proyectosConCodigo.length === 0 && (
                    <div className="px-3 py-2 text-xs text-slate-400">No hay proyectos con Código Inmobiliario.</div>
                  )}
                  {proyectosConCodigo.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.nombre} <span className="text-xs text-slate-400 font-mono">({p.codigo_proyecto})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Pertenece (subdivisión padre)</Label>
              <Popover open={perteneceOpen} onOpenChange={setPerteneceOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    className="w-full justify-between font-normal"
                    disabled={!formData.proyecto_id}
                    data-testid="subdivision-pertenece-trigger"
                  >
                    <span className="truncate">
                      {padreForm ? `${padreForm.codigo} — ${padreForm.nombre}` : 'Ninguna (raíz del proyecto)'}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Buscar subdivisión..." />
                    <CommandList>
                      <CommandEmpty>Sin subdivisiones previas.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem value="ninguna" onSelect={() => { setFormData({ ...formData, parent_id: '' }); setPerteneceOpen(false); }}>
                          <Check className={`mr-2 h-4 w-4 ${!formData.parent_id ? 'opacity-100' : 'opacity-0'}`} />
                          Ninguna (raíz del proyecto)
                        </CommandItem>
                        {subsDelProyecto
                          .filter((s) => !editing || s.id !== editing.id)
                          .map((s) => (
                            <CommandItem
                              key={s.id}
                              value={`${s.codigo} ${s.nombre}`}
                              onSelect={() => { setFormData({ ...formData, parent_id: s.id }); setPerteneceOpen(false); }}
                            >
                              <Check className={`mr-2 h-4 w-4 ${formData.parent_id === s.id ? 'opacity-100' : 'opacity-0'}`} />
                              <span className="font-mono text-xs mr-2">{s.codigo}</span>
                              <span className="truncate">{s.nombre}</span>
                            </CommandItem>
                          ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nombre">Nombre *</Label>
                <Input
                  id="nombre"
                  data-testid="subdivision-nombre-input"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  maxLength={30}
                  placeholder="Torre A"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Tipo *</Label>
                <Select value={formData.tipo} onValueChange={(v) => setFormData({ ...formData, tipo: v })}>
                  <SelectTrigger data-testid="subdivision-tipo-select">
                    <SelectValue placeholder="Seleccione" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPOS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="csd">Código *</Label>
                <label className="flex items-center gap-1.5 text-xs font-normal text-slate-600 cursor-pointer select-none">
                  <Checkbox
                    checked={formData.personalizar}
                    onCheckedChange={(v) => setFormData({ ...formData, personalizar: !!v })}
                    data-testid="subdivision-personalizar-check"
                  />
                  Personalizar
                </label>
              </div>
              {formData.personalizar ? (
                <>
                  <Input
                    id="codigo"
                    data-testid="subdivision-codigo-custom-input"
                    value={formData.codigo}
                    onChange={(e) => setFormData({ ...formData, codigo: e.target.value.toUpperCase() })}
                    maxLength={40}
                    placeholder="Código completo (ej. PAA-ESTA-E1)"
                    required
                  />
                  <p className="text-xs text-slate-400">
                    Código libre (no aplica la fórmula). El campo "Pertenece" define la jerarquía.
                  </p>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-1">
                    <span className="px-2.5 py-2 rounded-md bg-slate-100 text-slate-500 font-mono text-sm whitespace-nowrap" data-testid="subdivision-prefijo">
                      {prefijo || '(prefijo)'}-
                    </span>
                    <Input
                      id="csd"
                      data-testid="subdivision-csd-input"
                      value={formData.csd}
                      onChange={(e) => setFormData({ ...formData, csd: e.target.value.toUpperCase() })}
                      maxLength={40}
                      placeholder="E1"
                      required
                    />
                  </div>
                  <p className="text-xs text-slate-400">
                    Código final: <span className="font-mono">{prefijo ? `${prefijo}-${formData.csd || 'CSD'}` : '...'}</span>
                  </p>
                </>
              )}
            </div>

            <div className="space-y-2 max-w-[12rem]">
              <Label htmlFor="numero_pisos">Número de Pisos</Label>
              <Input
                id="numero_pisos"
                type="number"
                min="0"
                data-testid="subdivision-pisos-input"
                value={formData.numero_pisos}
                onChange={(e) => setFormData({ ...formData, numero_pisos: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="observaciones">Observaciones</Label>
              <Textarea
                id="observaciones"
                data-testid="subdivision-observaciones-input"
                value={formData.observaciones}
                onChange={(e) => setFormData({ ...formData, observaciones: e.target.value.slice(0, 1000) })}
                maxLength={1000}
                rows={3}
                placeholder="Notas adicionales (opcional)"
              />
              <p className="text-xs text-slate-400 text-right">{formData.observaciones.length}/1000</p>
            </div>

            <DialogFooter className="sticky bottom-0 -mx-6 -mb-6 px-6 py-3 bg-white border-t border-slate-100">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="subdivision-cancelar-btn">Cancelar</Button>
              <Button type="submit" disabled={saving} className="text-white" data-testid="subdivision-guardar-btn" {...getPrimaryButtonStyles(themeColors)}>
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando…</> : (editing ? 'Guardar' : 'Crear')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      {/* Import result dialog */}
      <Dialog open={!!importResult} onOpenChange={(o) => !o && setImportResult(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="import-result-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {importResult?.abortado
                ? <><XCircle className="w-5 h-5 text-red-500" /> Importación cancelada</>
                : <><CheckCircle2 className="w-5 h-5 text-green-600" /> Importación completada</>}
            </DialogTitle>
          </DialogHeader>
          {importResult && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="rounded-lg bg-green-50 p-3" data-testid="stat-insertadas">
                  <p className="text-2xl font-bold text-green-700">{importResult.insertadas}</p>
                  <p className="text-xs text-green-600 mt-1">Insertadas</p>
                </div>
                <div className="rounded-lg bg-blue-50 p-3" data-testid="stat-actualizadas">
                  <p className="text-2xl font-bold text-blue-700">{importResult.actualizadas}</p>
                  <p className="text-xs text-blue-600 mt-1">Actualizadas</p>
                </div>
                <div className="rounded-lg bg-red-50 p-3" data-testid="stat-errores">
                  <p className="text-2xl font-bold text-red-700">{importResult.errores?.length || 0}</p>
                  <p className="text-xs text-red-600 mt-1">Errores</p>
                </div>
              </div>
              {importResult.abortado && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Se encontraron errores. No se insertó ni actualizó ninguna subdivisión. Corrija el archivo y vuelva a intentarlo.
                  </AlertDescription>
                </Alert>
              )}
              {importResult.errores?.length > 0 && (
                <div className="border border-red-200 rounded-lg max-h-64 overflow-y-auto" data-testid="import-errores-list">
                  <ul className="divide-y divide-red-100">
                    {importResult.errores.map((err, i) => (
                      <li key={i} className="px-3 py-2 text-sm text-red-700">{err}</li>
                    ))}
                  </ul>
                </div>
              )}
              <DialogFooter>
                <Button onClick={() => setImportResult(null)} className="text-white" {...getPrimaryButtonStyles(themeColors)} data-testid="import-result-cerrar-btn">
                  Cerrar
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubdivisionesPage;
